import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import ExternalLink from "../../../../components/ExternalLink";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import MondayConnectForm from "./MondayConnectForm";
import monday_step_1 from "./images/monday_step_1.png";
import monday_step_2 from "./images/monday_step_2.png";
import monday_step_3 from "./images/monday_step_3.png";

const MondayIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("monday");
  const [loading, setLoading] = useState(false);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerMondayIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerMondayIntegration(values.api_token)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("monday.com integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading
            ? "Once integrated, the Breeze platform will automatically read the configurations from Monday and prioritize the suggested remediations."
            : ""
        }
        image={logos.MondayLogo}
        name={"Monday.com"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Monday is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login into your monday.com account">
                  <ExternalLink
                    href="https://monday.com/login"
                    text="1. Log in as an Admin user to"
                    linkText={"monday.com"}
                  />
                </GuidelineStep>
                <GuidelineStep title="Go to Administration settings">
                  <Typography variant={"body2"}>
                    In the top right corner, click on the <b>User Logo</b> and
                    then click on <strong>Administration</strong>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: monday_step_1,
                        altext: "monday.com administration settings",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Copy your API key">
                  <Typography variant={"body2"}>
                    In the side menu, click <b>Connections</b>, and go to the{" "}
                    <b>API </b>tab.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: monday_step_2,
                        altext: "monday.com Connections Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    You can generate a new API key by clicking on the{" "}
                    <b>Generate/Regenerate</b> button.
                  </Typography>
                  <Typography variant={"body2"}>
                    click Copy to copy the API key.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: monday_step_3,
                        altext: "monday.com API key copied",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the API key here">
                  <Box width={700}>
                    <MondayConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default MondayIntegration;
